export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Tempo de suavização da câmera ao seguir o alvo automático (jogador da vez ou projétil). */
export const CAMERA_FOLLOW_MS = 220;

/**
 * Largura do mapa que a tela mostra, em qualquer monitor: o palco do legado tem 1000 px (`Loading.swf`). O jogador mede a
 * distância pela tela, e a régua do minimapa divide essa largura em 10 (`SmallMapView.updateSpliter`).
 */
export const VIEW_WORLD_WIDTH = 1000;

/** Uma unidade de distância da régua: 1/10 da tela, 100 px do mapa. */
export const DISTANCE_UNIT = VIEW_WORLD_WIDTH / 10;

/** Escala da tela para que `view.width` mostre `worldWidth` px do mapa. Sem tela medida, 1. */
export function viewScale(view: Size, worldWidth = VIEW_WORLD_WIDTH): number {
  return view.width > 0 && worldWidth > 0 ? view.width / worldWidth : 1;
}

export interface CameraFocusInput {
  /** Tiro em reprodução: `released` é falso enquanto o atirador prepara o arremesso. */
  playback: { released: boolean } | null;
  /** Onde o projétil está agora (só vale depois da saída). */
  projectile: Point | null;
  /** O atirador do tiro em reprodução. */
  shooter: Point | null;
  /** Quem a câmera acompanha sem tiro no ar (o jogador da vez). */
  turnPlayer: Point | null;
  mapCenter: Point;
}

/**
 * O que a câmera acompanha. Preparando o arremesso fica no atirador, mesmo que o servidor já tenha passado a vez ao adversário;
 * com o projétil no ar, vai atrás dele; senão, no jogador da vez.
 */
export function cameraFocus(input: CameraFocusInput): Point {
  if (input.playback?.released && input.projectile) return input.projectile;
  if (input.playback && !input.playback.released && input.shooter) return input.shooter;
  return input.turnPlayer ?? input.mapCenter;
}

/**
 * Região do mapa que a câmera mostra, centrada em `center` e sempre dentro do mapa. `view` é a área livre da tela em pixels
 * (sem a faixa do painel de controles). Num eixo em que o mapa cabe inteiro, a região é o mapa todo nesse eixo.
 */
export function visibleRect(center: Point, map: Size, view: Size, scale: number): Rect {
  const width = Math.min(view.width / scale, map.width);
  const height = Math.min(view.height / scale, map.height);
  return {
    x: clamp(center.x - width / 2, 0, map.width - width),
    y: clamp(center.y - height / 2, 0, map.height - height),
    width,
    height,
  };
}

/**
 * Posição do contêiner do mundo na tela (o `world.position` do PixiJS) para mostrar `rect`. Num eixo em que o mapa é menor
 * que a tela, o mapa fica centrado nesse eixo.
 */
export function worldOffset(rect: Rect, map: Size, view: Size, scale: number): Point {
  const mapW = map.width * scale;
  const mapH = map.height * scale;
  return {
    x: mapW <= view.width ? (view.width - mapW) / 2 : -rect.x * scale,
    y: mapH <= view.height ? (view.height - mapH) / 2 : -rect.y * scale,
  };
}

/**
 * Câmera da partida, só no cliente: muda o que a tela mostra, nunca as coordenadas do jogo. A escala acompanha a tela para
 * mostrar sempre `worldWidth` px do mapa na largura. Dois modos:
 * - acompanhamento: segue o alvo automático com suavização;
 * - manual: fica onde o jogador deixou pelo minimapa, até `resumeFollow` (novo tiro, novo turno ou o próprio jogador andar).
 * Durante o arraste, `resumeFollow` só fica anotado e vale quando o arraste termina.
 */
export class Camera {
  private center: Point = { x: 0, y: 0 };
  private map: Size = { width: 0, height: 0 };
  private view: Size = { width: 0, height: 0 };
  private manualMode = false;
  private dragging = false;
  private resumeAfterDrag = false;

  constructor(readonly worldWidth = VIEW_WORLD_WIDTH) {}

  get scale(): number {
    return viewScale(this.view, this.worldWidth);
  }

  get manual(): boolean {
    return this.manualMode;
  }

  get isDragging(): boolean {
    return this.dragging;
  }

  /** Atualiza o tamanho do mapa e da área livre da tela; o centro volta para dentro dos limites. */
  setBounds(map: Size, view: Size): void {
    this.map = { width: Math.max(0, map.width), height: Math.max(0, map.height) };
    this.view = { width: Math.max(0, view.width), height: Math.max(0, view.height) };
    this.clampCenter();
  }

  /** Vai direto ao alvo e volta ao acompanhamento (início de partida, troca de mapa, reconexão). Cancela o arraste. */
  snapTo(target: Point): void {
    this.manualMode = false;
    this.dragging = false;
    this.resumeAfterDrag = false;
    this.center = { ...target };
    this.clampCenter();
  }

  /** Um quadro do acompanhamento: no modo manual não se move. */
  follow(target: Point, deltaMs: number): void {
    if (this.manualMode) return;
    const k = 1 - Math.exp(-Math.max(0, deltaMs) / CAMERA_FOLLOW_MS);
    this.center = { x: this.center.x + (target.x - this.center.x) * k, y: this.center.y + (target.y - this.center.y) * k };
    this.clampCenter();
  }

  /** Navegação manual: centra a câmera no ponto do mapa, sem suavização, dentro dos limites. */
  panTo(x: number, y: number): void {
    this.manualMode = true;
    this.center = { x, y };
    this.clampCenter();
  }

  beginDrag(): void {
    this.dragging = true;
    this.resumeAfterDrag = false;
  }

  /** Fim do arraste: se um evento pediu o acompanhamento durante o arraste, ele vale agora. */
  endDrag(): void {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.resumeAfterDrag) {
      this.resumeAfterDrag = false;
      this.manualMode = false;
    }
  }

  /** Volta a seguir o alvo automático; durante o arraste, espera o arraste terminar. */
  resumeFollow(): void {
    if (this.dragging) {
      this.resumeAfterDrag = true;
      return;
    }
    this.manualMode = false;
  }

  viewRect(): Rect {
    return visibleRect(this.center, this.map, this.view, this.scale);
  }

  offset(): Point {
    return worldOffset(this.viewRect(), this.map, this.view, this.scale);
  }

  /** Guarda o centro já limitado: perto da borda a câmera responde sem atraso quando o alvo volta para dentro. */
  private clampCenter(): void {
    const rect = this.viewRect();
    this.center = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
  }
}

/** Escala e tamanho do minimapa: o mapa inteiro dentro de `box`, com a proporção do mapa. */
export interface MinimapLayout {
  scale: number;
  width: number;
  height: number;
}

export function minimapLayout(map: Size, box: Size): MinimapLayout {
  if (map.width <= 0 || map.height <= 0 || box.width <= 0 || box.height <= 0) return { scale: 0, width: 0, height: 0 };
  const scale = Math.min(box.width / map.width, box.height / map.height);
  return { scale, width: map.width * scale, height: map.height * scale };
}

export function worldToMinimap(point: Point, layout: MinimapLayout): Point {
  return { x: point.x * layout.scale, y: point.y * layout.scale };
}

export function minimapToWorld(point: Point, layout: MinimapLayout): Point {
  if (layout.scale <= 0) return { x: 0, y: 0 };
  return { x: point.x / layout.scale, y: point.y / layout.scale };
}

export function rectToMinimap(rect: Rect, layout: MinimapLayout): Rect {
  return { x: rect.x * layout.scale, y: rect.y * layout.scale, width: rect.width * layout.scale, height: rect.height * layout.scale };
}

/**
 * Régua do minimapa, como a do legado: uma marca a cada `DISTANCE_UNIT` px a partir da borda esquerda da região visível,
 * de 1 a 9. Devolve o x de cada marca no mapa; num mapa mais estreito que a tela, só as marcas que cabem.
 */
export function rulerMarks(rect: Rect): { unit: number; x: number }[] {
  const marks: { unit: number; x: number }[] = [];
  for (let unit = 1; unit < 10 && unit * DISTANCE_UNIT < rect.width; unit++) marks.push({ unit, x: rect.x + unit * DISTANCE_UNIT });
  return marks;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
